import { Badge, Card, CardContent } from '@estac1/ui';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

interface ReviewItem {
  id: string;
  conversationId: string;
  lotName: string | null;
  question: string | null;
  answer: string;
  screen: string | null;
  blocked: string | null;
  detail: string | null;
  flag: string | null;
  flagReason: string | null;
  topicTitle: string | null;
  createdAt: string;
}

/** Por que a resposta pede revisão (#686, #688, #689). */
const REASON_LABEL: Record<string, string> = {
  gap: 'não soube responder',
  feedback: 'dono disse que não resolveu',
  commitment: 'pode ter prometido algo',
  off_topic: 'barrada como fora do assunto',
  injection: 'tentativa de ataque',
  refusal: 'recusada pelo modelo',
  error: 'falhou no meio',
  output: 'barrada na saída',
};

const when = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

/**
 * Respostas do último mês que pedem revisão (#686): o que o assistente não
 * soube, o que o dono reprovou e o que foi barrado, com o tema da triagem.
 */
export const ReviewList = ({ onOpen }: { onOpen: (conversationId: string) => void }) => {
  const review = useQuery({
    queryKey: ['admin-assistant-review'],
    queryFn: () => api.request<{ items: ReviewItem[] }>('/v1/admin/assistant/review'),
  });
  const items = review.data?.items ?? [];
  if (review.isLoading || items.length === 0) return null;

  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <h2 className="text-sm font-semibold">Para revisar ({items.length})</h2>
        <ul className="divide-y divide-border">
          {items.map((item) => {
            const reason = item.flag ?? item.blocked ?? '';
            return (
              <li key={item.id} className="space-y-1 py-2 text-sm">
                <p className="flex flex-wrap items-center gap-2 text-xs text-muted-fg">
                  <Badge variant={item.flag ? 'warning' : 'danger'}>
                    {REASON_LABEL[reason] ?? reason}
                  </Badge>
                  {item.lotName ?? 'Lote removido'} · {when(item.createdAt)}
                  {item.screen ? ` · ${item.screen}` : ''}
                  {item.topicTitle && <span>tema: {item.topicTitle}</span>}
                </p>
                <button
                  type="button"
                  className="block w-full truncate text-left hover:underline"
                  onClick={() => onOpen(item.conversationId)}
                >
                  {item.question ?? '(sem pergunta)'}
                </button>
                {(item.flagReason || item.detail) && (
                  <p className="text-xs text-muted-fg">{item.flagReason ?? item.detail}</p>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
};
